import { useMemo, useState } from "react";
import { Link, useSearch } from "wouter";
import { Plus, RefreshCw } from "lucide-react";
import { trpc } from "../lib/trpc";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { Button } from "../components/ui/button";
import { Select } from "../components/ui/input";
import { OrderListItem, OrderListEmpty } from "../components/order/OrderListItem";
import { ORDER_STATUSES, PENDING_DISPATCH_STATUSES, type OrderStatus, type Role } from "../../../shared/const";

type Loc = { "zh-TW": string; "en-US": string; "zh-CN": string };

interface Props {
  /** 後台用：覆寫狀態篩選（例：dispatcher 的「待派遣」用 pendingDispatch） */
  variant?: "all" | "pendingQuote" | "pendingDispatch" | "pendingForFleet";
  title?: Loc;
  subtitle?: Loc;
}

/**
 * D8 訂單分類頁籤（僅 variant="all"）：以狀態機為準（資料可靠），頁籤名稱對齊業主字眼。
 * 需求方＝已建立/進行中/已完成/全部；調度＝待派遣/進行中/已完成/全部；車隊＝進行中/已完成/全部。
 * 「服務日隔天自動轉已完成＋跨時區顯示」業主註明未來再議，本批不做。
 */
type TabDef = { key: string; label: Loc; statuses: OrderStatus[] | null }; // statuses=null → 全部
const CREATED_STATUSES: OrderStatus[] = [
  "pending",
  "quoting",
  "quoted",
  "rejected_quote",
  "confirmed",
  "dispatching",
  "rejected_dispatch",
];
const TAB_ALL: TabDef = { key: "all", label: { "zh-TW": "全部", "en-US": "All", "zh-CN": "全部" }, statuses: null };
const TAB_INFLIGHT: TabDef = { key: "inflight", label: { "zh-TW": "進行中", "en-US": "In service", "zh-CN": "进行中" }, statuses: ["active"] };
const TAB_DONE: TabDef = { key: "completed", label: { "zh-TW": "已完成", "en-US": "Completed", "zh-CN": "已完成" }, statuses: ["completed"] };

function tabsForRole(role: Role): TabDef[] {
  if (role === "dispatcher" || role === "admin") {
    return [
      { key: "pending_dispatch", label: { "zh-TW": "待派遣", "en-US": "Pending dispatch", "zh-CN": "待派遣" }, statuses: [...PENDING_DISPATCH_STATUSES] },
      TAB_INFLIGHT,
      TAB_DONE,
      TAB_ALL,
    ];
  }
  if (role === "fleet") {
    return [TAB_INFLIGHT, TAB_DONE, TAB_ALL];
  }
  // agency
  return [
    { key: "created", label: { "zh-TW": "已建立", "en-US": "Created", "zh-CN": "已建立" }, statuses: CREATED_STATUSES },
    TAB_INFLIGHT,
    TAB_DONE,
    TAB_ALL,
  ];
}

/** KPI 深連結 ?status= → 對應頁籤（狀態集合完全吻合者，否則全部） */
function tabKeyForStatusParam(tabs: TabDef[], statuses: OrderStatus[]): string {
  const set = new Set(statuses);
  const hit = tabs.find(
    (tb) => tb.statuses && tb.statuses.length === set.size && tb.statuses.every((s) => set.has(s)),
  );
  return (hit ?? TAB_ALL).key;
}

export default function OrdersList({ variant = "all", title, subtitle }: Props) {
  const { user } = useAuth();
  const { t } = useLanguage();
  const search = useSearch();
  const role = (user?.role ?? "agency") as Role;

  const searchParams = new URLSearchParams(search);
  const statusParam = searchParams.get("status");
  // ?filter=missing-driver：待補司機資訊（fleet 工作台 KPI 入口；active 且未回報）
  const missingDriver = variant === "all" && searchParams.get("filter") === "missing-driver";
  const urlStatuses =
    variant === "all" && statusParam
      ? (statusParam.split(",").filter((s) => (ORDER_STATUSES as readonly string[]).includes(s)) as OrderStatus[])
      : null;

  const tabs = useMemo(() => tabsForRole(role), [role]);
  const [activeKey, setActiveKey] = useState<string>(() =>
    missingDriver
      ? "inflight"
      : urlStatuses && urlStatuses.length
        ? tabKeyForStatusParam(tabs, urlStatuses)
        : tabs[0]!.key,
  );
  const [region, setRegion] = useState<"all" | "taiwan" | "hongkong">("all");
  const [sortBy, setSortBy] = useState<"created_desc" | "pickup_asc" | "pickup_desc">("created_desc");

  // 後端一次取全部（variant="all"），前端依頁籤/區域/排序過濾（KPI 深連結沿用，決定初始頁籤）。
  const myListQ = trpc.order.myList.useQuery(undefined, { enabled: variant === "all" });
  const pendingQuoteQ = trpc.order.pendingQuote.useQuery(undefined, { enabled: variant === "pendingQuote" });
  const pendingDispatchQ = trpc.order.pendingDispatch.useQuery(undefined, { enabled: variant === "pendingDispatch" });
  const pendingForFleetQ = trpc.order.pendingForFleet.useQuery(undefined, { enabled: variant === "pendingForFleet" });

  const q =
    variant === "pendingQuote"
      ? pendingQuoteQ
      : variant === "pendingDispatch"
        ? pendingDispatchQ
        : variant === "pendingForFleet"
          ? pendingForFleetQ
          : myListQ;

  const showTabs = variant === "all" && !missingDriver;
  // D5：區域篩選（軟篩選）— 訂單管理與調度工作台（待報價/待派遣）皆適用
  const showRegionFilter =
    !missingDriver &&
    (variant === "all" || variant === "pendingQuote" || variant === "pendingDispatch") &&
    (role === "dispatcher" || role === "admin");
  const activeTab = tabs.find((tb) => tb.key === activeKey) ?? tabs[0]!;

  // 區域過濾後的資料（供頁籤計數與清單共用）
  const regionRows = useMemo(() => {
    const rows = q.data ?? [];
    return region === "all" ? rows : rows.filter((o) => o.region === region);
  }, [q.data, region]);

  const tabCount = (tb: TabDef) =>
    tb.statuses ? regionRows.filter((o) => tb.statuses!.includes(o.status)).length : regionRows.length;

  const orders = useMemo(() => {
    if (missingDriver) {
      return [...(q.data ?? [])]
        .filter((o) => o.status === "active" && !o.driverInfoSubmittedAt)
        .sort((a, b) => a.pickupTime - b.pickupTime);
    }
    // regionRows 已套用 D5 區域過濾（variant=all 與調度工作台皆適用）
    let rows = showTabs && activeTab.statuses ? regionRows.filter((o) => activeTab.statuses!.includes(o.status)) : regionRows;
    if (showTabs && sortBy === "pickup_asc") rows = [...rows].sort((a, b) => a.pickupTime - b.pickupTime);
    else if (showTabs && sortBy === "pickup_desc") rows = [...rows].sort((a, b) => b.pickupTime - a.pickupTime);
    // created_desc / pending 變體：維持後端排序
    return rows;
  }, [missingDriver, showTabs, q.data, activeTab, regionRows, sortBy]);

  const effectiveTitle =
    title ??
    (missingDriver
      ? ({ "zh-TW": "待補司機資訊", "en-US": "Missing driver info", "zh-CN": "待补司机资讯" } as Loc)
      : ({ "zh-TW": "訂單", "en-US": "Orders", "zh-CN": "订单" } as Loc));
  const effectiveSubtitle =
    subtitle ??
    (missingDriver
      ? ({
          "zh-TW": "服務中但尚未回報司機姓名／電話／車號的訂單。出發前 24 小時須填妥。",
          "en-US": "Active orders without driver name / phone / plate. Due 24h before departure.",
          "zh-CN": "服务中但尚未回报司机姓名／电话／车号的订单。出发前 24 小时须填妥。",
        } as Loc)
      : undefined);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-medium tracking-tight">{t(effectiveTitle)}</h1>
          {effectiveSubtitle && <p className="text-[13px] text-muted-foreground">{t(effectiveSubtitle)}</p>}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => q.refetch()}>
            <RefreshCw className={`h-3.5 w-3.5 ${q.isFetching ? "animate-spin" : ""}`} />
            {t({ "zh-TW": "重新整理", "en-US": "Refresh", "zh-CN": "重新整理" })}
          </Button>
          {user?.role === "agency" && (
            <Link href="/orders/new">
              <Button size="sm">
                <Plus className="h-3.5 w-3.5" />
                {t({ "zh-TW": "新增訂單", "en-US": "New request", "zh-CN": "新增订单" })}
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* D8 分類頁籤 ＋ D5 區域篩選 ＋ 日期排序 */}
      {(showTabs || showRegionFilter) && (
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border pb-2">
          <div className="flex flex-wrap gap-1">
            {showTabs &&
              tabs.map((tb) => {
                const on = tb.key === activeKey;
                return (
                  <button
                    key={tb.key}
                    type="button"
                    onClick={() => setActiveKey(tb.key)}
                    className={`rounded-md px-3 py-1.5 text-[13px] transition-colors ${
                      on ? "bg-primary-soft font-medium text-primary-soft-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    }`}
                  >
                    {t(tb.label)}
                    <span className="ml-1.5 font-mono text-[11px] opacity-70">{tabCount(tb)}</span>
                  </button>
                );
              })}
          </div>
          <div className="flex items-center gap-2">
            {showRegionFilter && (
              <Select
                className="h-8 w-28"
                value={region}
                onChange={(e) => setRegion(e.target.value as "all" | "taiwan" | "hongkong")}
                aria-label={t({ "zh-TW": "區域篩選", "en-US": "Region filter", "zh-CN": "区域筛选" })}
              >
                <option value="all">{t({ "zh-TW": "全部區域", "en-US": "All regions", "zh-CN": "全部区域" })}</option>
                <option value="taiwan">{t({ "zh-TW": "台灣", "en-US": "Taiwan", "zh-CN": "台湾" })}</option>
                <option value="hongkong">{t({ "zh-TW": "香港", "en-US": "Hong Kong", "zh-CN": "香港" })}</option>
              </Select>
            )}
            {showTabs && (
              <Select
                className="h-8 w-32"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                aria-label={t({ "zh-TW": "排序", "en-US": "Sort", "zh-CN": "排序" })}
              >
                <option value="created_desc">{t({ "zh-TW": "最新建立", "en-US": "Newest", "zh-CN": "最新建立" })}</option>
                <option value="pickup_asc">{t({ "zh-TW": "服務日近→遠", "en-US": "Service date ↑", "zh-CN": "服务日近→远" })}</option>
                <option value="pickup_desc">{t({ "zh-TW": "服務日遠→近", "en-US": "Service date ↓", "zh-CN": "服务日远→近" })}</option>
              </Select>
            )}
          </div>
        </div>
      )}

      {q.isLoading ? (
        <div className="h-24 animate-pulse rounded-lg bg-muted/40" />
      ) : orders.length === 0 ? (
        <OrderListEmpty />
      ) : (
        <div className="space-y-2">
          {orders.map((o) => (
            <OrderListItem
              key={o.id}
              order={{
                ...o,
                finalPrice: o.finalPrice ?? null,
                fleetPayout: o.fleetPayout ?? null,
                fleetPayoutCurrency: o.fleetPayoutCurrency ?? null,
                fleetId: o.fleetId ?? null,
                agencyName: o.agencyName ?? undefined,
                fleetName: o.fleetName ?? undefined,
                // 178 反饋 A2/C5：起迄點與司機資訊（四方可見，不在金額裁剪範圍）
                dropoffLocation: o.dropoffLocation ?? null,
                driverName: o.driverName ?? null,
                driverPhone: o.driverPhone ?? null,
                vehiclePlate: o.vehiclePlate ?? null,
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
